import { useMemo } from 'react';
import { useCrm } from '../store';
import { Card, PageTitle, StatutBadge, Tag } from '../ui';
import { formatDateFR, formatEUR } from '../utils';
import { Theater, Archive } from 'lucide-react';
import { ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, CartesianGrid, Legend } from 'recharts';
import { toast } from 'sonner';
import { useConfirm } from '../components/ConfirmDialog';

const MONTHS = ['Jan', 'Fév', 'Mar', 'Avr', 'Mai', 'Juin', 'Juil', 'Août', 'Sep', 'Oct', 'Nov', 'Déc'];
const COLORS = ['#f59e0b', '#10b981', '#0ea5e9', '#a855f7', '#ef4444', '#06b6d4', '#eab308', '#ec4899'];

const formatDateTimeFR = (iso?: string | null) => {
  if (!iso) return '—';
  const d = new Date(iso);
  if (isNaN(d.getTime())) return '—';
  return d.toLocaleString('fr-FR', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' });
};

export default function SuiviDomaines({ embedded = false }: { embedded?: boolean } = {}) {
  const data = useCrm();
  const confirm = useConfirm();

  const grouped = useMemo(() => {
    const map = new Map<string, typeof data.prest>();
    for (const p of data.prest) {
      if (p.archivedAt) continue;
      for (const d of p.domaines) {
        const arr = map.get(d) ?? [];
        arr.push(p);
        map.set(d, arr);
      }
    }
    return Array.from(map.entries())
      .map(([name, prests]) => [name, [...prests].sort((a, b) => {
        const t = Date.now();
        const da = a.dateEvent ? Math.abs(new Date(a.dateEvent).getTime() - t) : Infinity;
        const db = b.dateEvent ? Math.abs(new Date(b.dateEvent).getTime() - t) : Infinity;
        return da - db;
      })] as const)
      .sort((a, b) => a[0].localeCompare(b[0]));
  }, [data.prest]);

  // Saisonnalité 12 derniers mois × top 6 domaines (CDC §8)
  const saisonnalite = useMemo(() => {
    const top6 = grouped
      .map(([nom, prests]) => ({ nom, ca: prests.reduce((s, p) => s + (p.propTTC ?? 0), 0) }))
      .sort((a, b) => b.ca - a.ca)
      .slice(0, 6)
      .map((d) => d.nom);
    const now = new Date();
    const buckets: Record<string, any> = {};
    for (let i = 11; i >= 0; i--) {
      const dt = new Date(now.getFullYear(), now.getMonth() - i, 1);
      const key = `${dt.getFullYear()}-${String(dt.getMonth() + 1).padStart(2, '0')}`;
      const row: any = { mois: `${MONTHS[dt.getMonth()]} ${String(dt.getFullYear()).slice(2)}` };
      top6.forEach((d) => (row[d] = 0));
      buckets[key] = row;
    }
    for (const p of data.prest) {
      if (!p.dateEvent || p.statut !== 'Signé') continue;
      const k = p.dateEvent.slice(0, 7);
      if (!buckets[k]) continue;
      for (const d of p.domaines) {
        if (top6.includes(d)) buckets[k][d] += p.propTTC ?? 0;
      }
    }
    return { rows: Object.values(buckets), domaines: top6 };
  }, [data.prest, grouped]);

  return (
    <div>
      {!embedded && <PageTitle title="Suivi Domaines" subtitle="Activité par domaine artistique" icon={<Theater className="text-amber-400" size={24} strokeWidth={1.5} />} />}

      <div className="grid grid-cols-3 gap-3 mb-6">
        <Card className="p-4"><div className="text-[10px] text-slate-400 uppercase tracking-widest mb-2">Domaines</div><div className="font-heading text-3xl text-amber-400">{grouped.length}</div></Card>
        <Card className="p-4"><div className="text-[10px] text-slate-400 uppercase tracking-widest mb-2">Prestations</div><div className="font-heading text-3xl text-sky-400">{data.prest.length}</div></Card>
        <Card className="p-4"><div className="text-[10px] text-slate-400 uppercase tracking-widest mb-2">CA Total</div><div className="font-heading text-3xl text-emerald-400">{formatEUR(data.prest.reduce((s, p) => s + (p.propTTC ?? 0), 0))}</div></Card>
      </div>

      {/* Saisonnalité par domaine — CDC §8 */}
      <Card className="p-6 mb-6">
        <div className="text-xs text-slate-400 uppercase tracking-widest mb-4">
          Saisonnalité — CA signé par domaine (12 derniers mois, top 6)
        </div>
        <div className="h-72">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={saisonnalite.rows}>
              <CartesianGrid strokeDasharray="3 3" stroke="#1e293b" />
              <XAxis dataKey="mois" tick={{ fill: '#94a3b8', fontSize: 11 }} />
              <YAxis tick={{ fill: '#94a3b8', fontSize: 11 }} tickFormatter={(v) => `${(v / 1000).toFixed(0)}k`} />
              <Tooltip
                contentStyle={{ backgroundColor: '#0f172a', border: '1px solid #334155', borderRadius: 8, fontSize: 12 }}
                cursor={{ fill: 'rgba(245, 158, 11, 0.08)' }}
                formatter={(v: number) => formatEUR(v)}
              />
              <Legend wrapperStyle={{ fontSize: 11, color: '#94a3b8' }} />
              {saisonnalite.domaines.map((d, i) => (
                <Bar key={d} dataKey={d} stackId="a" fill={COLORS[i % COLORS.length]} />
              ))}
            </BarChart>
          </ResponsiveContainer>
        </div>
      </Card>


      <div className="space-y-4">
        {grouped.map(([name, prests]) => {
          const ca = prests.reduce((s, p) => s + (p.propTTC ?? 0), 0);
          const artistes = Array.from(new Set(prests.flatMap((p) => p.artistes)));
          const clients = new Set(prests.map((p) => p.client)).size;
          return (
            <Card key={name} className="overflow-hidden">
              <div className="p-4 border-b border-slate-800 flex items-center justify-between">
                <div>
                  <div className="font-heading text-lg text-slate-100 flex items-center gap-2"><Theater size={16} className="text-amber-400" strokeWidth={1.5} /> {name}</div>
                  <div className="flex flex-wrap gap-1.5 mt-2">{artistes.map((a) => <Tag key={a} color="amber">{a}</Tag>)}</div>
                </div>
                <div className="flex gap-3 text-center">
                  <div><div className="text-[10px] text-slate-500 uppercase">CA</div><div className="text-amber-400 text-sm font-medium">{formatEUR(ca)}</div></div>
                  <div><div className="text-[10px] text-slate-500 uppercase">Clients</div><div className="text-slate-300 text-sm font-medium">{clients}</div></div>
                  <div><div className="text-[10px] text-slate-500 uppercase">Presta</div><div className="text-slate-300 text-sm font-medium">{prests.length}</div></div>
                </div>
              </div>
              <div className="overflow-x-auto"><table className="w-full text-sm min-w-[860px]">
                <thead className="bg-slate-900/40 text-[10px] text-slate-500 uppercase tracking-wider">
                  <tr>
                    <th className="px-4 py-2 text-left">Créée le</th>
                    <th className="px-4 py-2 text-left">Date event</th>
                    <th className="px-4 py-2 text-left">Client</th>
                    <th className="px-4 py-2 text-left">Type</th>
                    <th className="px-4 py-2 text-left">Statut</th>
                    <th className="px-4 py-2 text-left">Lieu</th>
                    <th className="px-4 py-2 text-left">Artistes</th>
                    <th className="px-4 py-2 text-right">Prop. TTC</th>
                    <th className="px-4 py-2 text-right w-10"></th>
                  </tr>
                </thead>
                <tbody>
                  {prests.map((p) => (
                    <tr key={p.id} className="border-t border-slate-800/60 hover:bg-slate-800/30">
                      <td className="px-4 py-2.5 text-xs text-slate-400 whitespace-nowrap">{formatDateTimeFR(p.dateCreation)}</td>
                      <td className="px-4 py-2.5 text-xs text-slate-400">{formatDateFR(p.dateEvent)}</td>
                      <td className="px-4 py-2.5 text-xs text-slate-300 font-medium">{p.client}</td>
                      <td className="px-4 py-2.5 text-xs text-slate-300">{p.libelle}</td>
                      <td className="px-4 py-2.5"><StatutBadge statut={p.statut} /></td>
                      <td className="px-4 py-2.5 text-xs text-slate-400">{p.lieu || '—'}</td>
                      <td className="px-4 py-2.5 text-xs text-amber-300">{p.artistes.join(', ') || '—'}</td>
                      <td className="px-4 py-2.5 text-xs text-right text-amber-400">{p.propTTC != null ? formatEUR(p.propTTC) : '—'}</td>
                      <td className="px-2 py-2.5 text-right">
                        <button
                          title="Archiver cette proposition"
                          onClick={async () => {
                            const ok = await confirm({
                              title: 'Archiver cette proposition ?',
                              description: `Elle disparaîtra du suivi actif (visible dans Archives). ${p.libelle || ''}`,
                              confirmLabel: 'Archiver',
                            });
                            if (!ok) return;
                            await (data as any).archivePrest(p.id);
                            toast.success('Proposition archivée');
                          }}
                          className="p-1.5 rounded-md text-slate-500 hover:text-amber-400 hover:bg-slate-800 transition-colors"
                        >
                          <Archive size={14} strokeWidth={1.5} />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table></div>
            </Card>
          );
        })}
      </div>
    </div>
  );
}
